import { Bot, Clock3, User, Wrench } from 'lucide-react';
import { type ReactNode } from 'react';

import { ChatMessage } from '../../components/chat/ChatMessage';
import { type SessionTimelineEntry } from '../../services/api/sessions';
import { cn } from '../../utils/cn';

import { shownChips, speakerLabel, toCustomerMessage } from './customer-view';
import { FlagButton } from './FlagButton';
import { flagTextOf } from './timeline-content';
import { TimelineAttachments } from './TimelineAttachments';
import { TurnDebugPanel } from './TurnDebugPanel';

// Latens och verktyg låg tidigare under metadata.timings och metadata.sources.
// Båda är borta ur datan sedan länge — fälten bor i _debug, som också är det
// sessionsrutten räknar "Snittsvar" på.
const MessageMeta = ({ entry }: { entry: SessionTimelineEntry }) => {
  const totalMs = entry.metadata?._debug?.latency?.totalMs;
  const toolCount = entry.metadata?._debug?.tools?.length ?? 0;

  if (typeof totalMs !== 'number' && toolCount === 0) return null;

  return (
    <div className="mt-2 flex flex-wrap gap-2 text-[11px] text-slate-500">
      {typeof totalMs === 'number' && (
        <span className="inline-flex items-center gap-1 rounded-full bg-white/70 px-2 py-1">
          <Clock3 className="h-3 w-3" />
          {Math.round(totalMs)} ms
        </span>
      )}
      {toolCount > 0 && (
        <span className="inline-flex items-center gap-1 rounded-full bg-white/70 px-2 py-1">
          <Wrench className="h-3 w-3" />
          {toolCount} verktyg
        </span>
      )}
    </div>
  );
};

const ShownChips = ({ entry }: { entry: SessionTimelineEntry }) => {
  const chips = shownChips(entry);
  if (chips.length === 0) return null;

  return (
    <div className="mt-2">
      <div className="text-[11px] font-medium text-slate-500">Knappar kunden fick</div>
      <div className="mt-1 flex flex-wrap gap-1.5">
        {chips.map((chip, index) => (
          <span
            key={`${chip.value}-${index}`}
            title={chip.value}
            className="rounded-full border border-slate-300 bg-white px-2.5 py-1 text-xs text-slate-700"
          >
            {chip.label}
          </span>
        ))}
      </div>
    </div>
  );
};

interface TimelineMessageProps {
  entry: SessionTimelineEntry;
  sessionId: string;
  canFlag: boolean;
  precedingUserMessage: string;
  language?: string;
  footer?: ReactNode;
}

export const TimelineMessage = ({
  entry,
  sessionId,
  canFlag,
  precedingUserMessage,
  language,
  footer,
}: TimelineMessageProps) => {
  const isAssistant = entry.role === 'assistant';
  const customerMessage = toCustomerMessage(entry, language);

  if (entry.role === 'system') {
    return (
      <div
        data-turn-sequence={entry.sequence}
        data-turn-index={entry.sourceIndex}
        className="flex w-full justify-center"
      >
        <div
          className={cn(
            'max-w-[88%] rounded-2xl border px-4 py-3 shadow-sm',
            entry.fromAgent
              ? 'border-emerald-200 bg-emerald-50 text-emerald-900'
              : 'border-amber-200 bg-amber-50 text-amber-900',
          )}
        >
          <div className="flex items-center gap-2 text-xs opacity-80">
            <span className="font-semibold">{speakerLabel(entry)}</span>
            <span>&bull;</span>
            <span>{new Date(entry.createdAt).toLocaleString('sv-SE')}</span>
          </div>
          <div className="mt-2 whitespace-pre-wrap break-words text-sm leading-6">{entry.content}</div>
        </div>
      </div>
    );
  }

  const flagText = isAssistant ? flagTextOf(entry.content) : entry.content;

  return (
    <div
      data-turn-sequence={entry.sequence}
      data-turn-index={entry.sourceIndex}
      className="w-full"
    >
      <div
        className={cn(
          'mb-1 flex items-center gap-2 text-xs text-slate-500',
          isAssistant ? 'justify-start' : 'justify-end',
        )}
      >
        {isAssistant ? <Bot className="h-3.5 w-3.5" /> : <User className="h-3.5 w-3.5" />}
        <span className="font-semibold capitalize">{entry.role}</span>
        <span>&bull;</span>
        <span>{new Date(entry.createdAt).toLocaleString('sv-SE')}</span>
      </div>

      {/* Skrivskyddat: utan handler och med isLatestAssistant=false ritar
          ChatMessage varken klickbara knappar eller ett skickbart mejlutkast. */}
      {customerMessage ? (
        <ChatMessage message={customerMessage} isLatestAssistant={false} />
      ) : (
        <div className="flex justify-end">
          <div className="max-w-[88%] whitespace-pre-wrap break-words rounded-sm border border-brand-blue bg-brand-blue px-4 py-3 text-[15px] leading-7 text-white">
            {entry.content}
          </div>
        </div>
      )}

      {entry.role === 'user' && (
        <div className="flex justify-end">
          <TimelineAttachments attachments={entry.attachments ?? []} />
        </div>
      )}

      {isAssistant && (
        <div className="max-w-[88%]">
          <ShownChips entry={entry} />
          <MessageMeta entry={entry} />
          {entry.metadata?._debug && <TurnDebugPanel debug={entry.metadata._debug} />}
          {footer}
          {canFlag && (
            <div className="mt-2 border-t border-slate-100 pt-2">
              <FlagButton
                sessionId={sessionId}
                userMessage={precedingUserMessage}
                assistantAnswer={flagText || entry.content}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};
